import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { EXTERNAL_SUPABASE_URL, EXTERNAL_SUPABASE_PUBLISHABLE_KEY } from "@/integrations/external-supabase/client";
import { safeReturnPath } from "@/lib/safeReturnPath";

// #740 (Option B) — exchange now goes to kroger-token/index.ts, scoped to
// her real session, and stores the tokens server-side. This page no longer
// writes anything to localStorage — useKroger.tsx's checkStatus() picks up
// the new connection on its own next mount, same as useGoogleCalendar.tsx's
// callback page never needed to hand state back manually either.
const KROGER_TOKEN_URL = `${EXTERNAL_SUPABASE_URL}/functions/v1/kroger-token`;
const REDIRECT_URI = `${window.location.origin}/kroger/callback`;

function readAndClearReturnPath(): string | null {
  try {
    const path = sessionStorage.getItem("kin_kroger_return_path");
    if (path) {
      sessionStorage.removeItem("kin_kroger_return_path");
      // ⚠️ Sanitise before it reaches navigate(). The stored value is her own
      // pathname, but a pathname CAN begin with "//" (visit
      // https://app.kinyoudoitall.com//evil.example and pathname is
      // "//evil.example"), which react-router reinterprets as a
      // protocol-relative URL and follows off-origin — GHSA-2j2x-hqr9-3h42.
      // Clearing the key first is deliberate: a value we refuse must not
      // survive to be retried on the next callback.
      return safeReturnPath(path, window.location.origin);
    }
  } catch {
    // private browsing
  }
  return null;
}

export default function KrogerCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { session, loading: authLoading } = useAuth();
  const [status, setStatus] = useState<"connecting" | "success" | "error">(
    "connecting"
  );
  const [errorMessage, setErrorMessage] = useState<string>("");

  const code = searchParams.get("code");
  const errorParam = searchParams.get("error");
  const errorDescription = searchParams.get("error_description");

  useEffect(() => {
    // OAuth provider returned an explicit error (user denied, etc.)
    if (errorParam) {
      setErrorMessage(
        errorDescription ||
          "We couldn't connect to Kroger. You can try again anytime."
      );
      setStatus("error");
      return;
    }

    // No code = malformed callback. Send them home with no fanfare.
    if (!code) {
      const returnPath = readAndClearReturnPath() || "/";
      navigate(returnPath, { replace: true });
      return;
    }

    // Wait for AuthProvider's own getSession() to settle — the exchange
    // needs her real session JWT, and this page can mount before that
    // resolves right after the full-page OAuth redirect back.
    if (authLoading) return;
    if (!session?.access_token) {
      setErrorMessage("Your session expired. Sign back in and try connecting again.");
      setStatus("error");
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(KROGER_TOKEN_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
            apikey: EXTERNAL_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({ action: "exchange", code, redirectUri: REDIRECT_URI }),
        });

        const data = await resp.json();
        if (cancelled) return;

        if (resp.ok && data.success) {
          setStatus("success");
          setTimeout(() => {
            if (cancelled) return;
            const returnPath = readAndClearReturnPath() || "/";
            navigate(returnPath, { replace: true });
          }, 900);
        } else {
          setErrorMessage(
            "Something went wrong on our end. Try connecting again."
          );
          setStatus("error");
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Kroger auth callback error:", err);
        setErrorMessage(
          "Couldn't reach the server. Check your connection and try again."
        );
        setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [code, errorParam, errorDescription, navigate, authLoading, session?.access_token]);

  return (
    <div className="flex items-center justify-center h-screen bg-background">
      <div
        className="text-center"
        style={{
          maxWidth: 320,
          padding: 24,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 12,
        }}
        role="status"
        aria-live="polite"
      >
        {status === "connecting" && (
          <>
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-current border-t-transparent" style={{ color: "#5050C8" }} />
            <p
              style={{
                fontFamily: '"Plus Jakarta Sans", "Montserrat", sans-serif',
                fontSize: 15,
                color: "#54525C",
              }}
            >
              Connecting your Kroger account...
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <div
              style={{
                fontSize: 32,
                lineHeight: 1,
              }}
              aria-hidden="true"
            >
              ✓
            </div>
            <div
              style={{
                fontFamily: '"DM Serif Display", serif',
                fontSize: 22,
                color: "#1C1A3E",
              }}
            >
              Kroger connected
            </div>
            <div
              style={{
                fontFamily: '"Instrument Serif", serif',
                fontStyle: "italic",
                fontSize: 15,
                color: "#5050C8",
              }}
            >
              Heading back, mama 💜
            </div>
          </>
        )}

        {status === "error" && (
          <>
            <div
              style={{
                fontFamily: '"DM Serif Display", serif',
                fontSize: 22,
                color: "#1C1A3E",
              }}
            >
              Couldn't connect
            </div>
            <div
              style={{
                fontFamily: '"Plus Jakarta Sans", "Montserrat", sans-serif',
                fontSize: 14,
                color: "#54525C",
                lineHeight: 1.4,
              }}
            >
              {errorMessage}
            </div>
            <button
              onClick={() => {
                const returnPath = readAndClearReturnPath() || "/";
                navigate(returnPath, { replace: true });
              }}
              style={{
                marginTop: 8,
                padding: "10px 20px",
                backgroundColor: "#5050C8",
                color: "white",
                border: "none",
                borderRadius: 8,
                fontFamily: '"Plus Jakarta Sans", "Montserrat", sans-serif',
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
                minHeight: 44,
              }}
            >
              Got it, take me back
            </button>
          </>
        )}
      </div>
    </div>
  );
}
